import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { triggerBlobDownload } from '@/app/shared/utils/file-download.util';
import { Payroll, PayrollPdf } from '../models/payroll.model';
import { PayrollRepository } from '../services/payroll.repository';

export type PayrollPdfStatus = 'idle' | 'loading' | 'ready' | 'error';

@Injectable()
export class PayrollPdfStore {
  private readonly repository = inject(PayrollRepository);

  private readonly _status = signal<PayrollPdfStatus>('idle');
  private readonly _payroll = signal<Payroll | null>(null);
  private readonly _objectUrl = signal<string | null>(null);
  private pdf: PayrollPdf | null = null;
  private inFlight?: Subscription;

  readonly status = this._status.asReadonly();
  readonly payroll = this._payroll.asReadonly();
  readonly objectUrl = this._objectUrl.asReadonly();

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.inFlight?.unsubscribe();
      this.revokeUrl();
    });
  }

  open(payroll: Payroll): void {
    this.inFlight?.unsubscribe();
    this.revokeUrl();
    this.pdf = null;
    this._payroll.set(payroll);
    this._status.set('loading');

    this.inFlight = this.repository.getPdf(payroll).subscribe({
      next: (pdf) => {
        this.pdf = pdf;
        this._objectUrl.set(URL.createObjectURL(pdf.blob));
        this._status.set('ready');
      },
      error: () => this._status.set('error'),
    });
  }

  close(): void {
    this.inFlight?.unsubscribe();
  }

  download(): void {
    if (this.pdf) triggerBlobDownload(this.pdf.blob, this.pdf.fileName);
  }

  private revokeUrl(): void {
    const url = this._objectUrl();
    if (url) URL.revokeObjectURL(url);
    this._objectUrl.set(null);
  }
}
